<template>
  <div class="flex h-screen overflow-hidden">
    <!-- 사이드바: 항상 렌더링 -->
    <AppSidebar
      :token-usage="tokenUsage"
      :show-chat-features="false"
      :show-history-panel="false"
      :playground-info-text="'서로 다른 LLM 모델의 응답을\n한눈에 비교해 보세요.'"
      @new-chat="createNewChat"
    />
    
    <!-- 메인 컨테이너 -->
    <div class="flex-1 flex flex-col">
      <!-- 상단 네비게이션 -->
      <div class="flex items-center justify-between p-4 bg-white relative z-[100]">
        <div class="flex items-center gap-4">
          <button
            v-if="isSidebarHidden"
            @click="toggleSidebar"
            title="사이드바 열기"
          >
            <img src="@/assets/icons/bar_side.png" alt="사이드바 열기" class="h-6 w-6" />
          </button>
        </div>
        <div class="flex items-center gap-4 debug-disabled">
          <AppHamburgerMenu
            :show-debug="false"
            :debug-mode="debugMode"
            @new-chat="createNewChat"
          />
          <AppUserMenu
            :user-name="userName"
            :user-email="userEmail"
            :debug-mode="debugMode"
            @logout="logout"
          />
        </div>
      </div>

      <!-- 메인 콘텐츠 영역 -->
      <div class="flex-1 flex flex-col min-h-0 pl-0 z-0" :class="{ 'pl-10': isSidebarHidden }">
        <!-- 채팅 컨테이너 -->
        <div class="flex-1 flex flex-col min-h-0">
          <!-- 채팅 영역 -->
          <div class="flex-1 grid grid-cols-2 min-h-0">
            <!-- 왼쪽 채팅 영역 -->
            <div class="flex flex-col min-h-0">
              <!-- 모델 선택 -->
              <div class="p-2 ml-1 bg-white" :class="{ 'pl-10': isSidebarHidden }">
                <div class="w-[400px] relative" style="z-index: 9999;" v-if="!showHistoryPanel">
                  <AppModelSelect
                    :selected-model="leftModel"
                    :placeholder="leftModelPlaceholder"
                    :dropdown-open="openedDropdown === 'leftModel'"
                    @select-model="selectLeftModel"
                    @dropdown-opened="onDropdownOpened('leftModel')"
                  />
                </div>
              </div>
              <!-- 채팅 메시지 -->
              <div class="flex-1 overflow-y-auto overflow-x-hidden p-4 bg-white" ref="leftChatContainer">
                <div class="w-full max-w-[600px] mx-auto min-h-[48px] flex flex-col h-full" :class="{ 'pl-10': isSidebarHidden }">
                  <div v-if="leftMessages.length === 0" class="flex flex-col items-center justify-center h-full">
                    <h1 class="text-2xl font-bold text-gray-700 mb-8" title="새로운 대화 시작">무엇을 도와드릴까요?</h1>
                  </div>
                  <div v-else>
                    <ChatMessage
                      ref="leftChatMessage"
                      :messages="leftMessages"
                      :debug-mode="false"
                      :show-date="false"
                      :is-waiting-response="isWaitingResponse"
                      @edit-message="handleEditLeftMessage"
                      @show-error-modal="handleErrorModal"
                      @show-filtering-modal="handleFilteringModal"
                      @show-masking-modal="handleMaskingModal"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- 오른쪽 채팅 영역 (구분선 위치 조정) -->
            <div class="flex flex-col min-h-0">
              <!-- 모델 선택 -->
              <div class="p-2 ml-3 bg-white">
                <div class="w-[400px] relative" style="z-index: 9999;" v-if="!showHistoryPanel">
                  <AppModelSelect
                    :selected-model="rightModel"
                    :placeholder="rightModelPlaceholder"
                    :dropdown-open="openedDropdown === 'rightModel'"
                    @select-model="selectRightModel"
                    @dropdown-opened="onDropdownOpened('rightModel')"
                  />
                </div>
              </div>
              <!-- 채팅 메시지 (여기에만 border-l) -->
              <div class="flex-1 overflow-y-auto overflow-x-hidden p-4 bg-white border-l border-gray-200" ref="rightChatContainer">
                <div class="w-full max-w-[600px] mx-auto min-h-[48px] flex flex-col h-full">
                  <div v-if="rightMessages.length === 0" class="flex flex-col items-center justify-center h-full">
                    <h1 class="text-2xl font-bold text-gray-700 mb-8" title="새로운 대화 시작">무엇을 도와드릴까요?</h1>
                  </div>
                  <div v-else>
                    <ChatMessage
                      ref="rightChatMessage"
                      :messages="rightMessages"
                      :debug-mode="false"
                      :show-date="false"
                      :is-waiting-response="isWaitingResponse"
                      @edit-message="handleEditRightMessage"
                      @show-error-modal="handleErrorModal"
                      @show-filtering-modal="handleFilteringModal"
                      @show-masking-modal="handleMaskingModal"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 메시지 입력 영역 -->
          <div class="p-4 bg-white">
            <div class="w-full max-w-[600px] mx-auto min-h-[48px]">
              <ChatInput
                ref="chatInput"
                :validation-fn="validateBeforeSend"
                @send-message="handleMessageSuccess"
                @pause="handlePause"
                :is-waiting-response="isWaitingResponse"
                :is-typing="isWaitingResponse"
                :messages="[...leftMessages, ...rightMessages]"
                :disabled="!canSendMessage"
                :min-height="48"
                :chat-id="'playground_disabled'"
                :model-name="leftModel?.name || rightModel?.name || 'playground'"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showModelSelectModal" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
      <div
        class="bg-white rounded-lg p-6 shadow-lg text-center"
        style="min-width: 100px; max-width: 90vw; width: 400px;"
      >
        <div class="mb-4 text-base font-semibold" title="모델 선택">모델을 선택해주세요</div>
        <button
          class="px-2 py-1 bg-blue-100 hover:bg-blue-200 rounded"
          @click="showModelSelectModal = false" title="확인">확인</button>
      </div>
    </div>
    <AlertModal :visible="showAlertModal" :message="alertMessage" @confirm="showAlertModal = false" />
    
    <!-- 마스킹 모달 -->
    <AlertModal
      v-if="showMaskingModal"
      :visible="showMaskingModal"
      :message="''"
      size="wide"
    >
      <template #default>
        <div class="mb-2 mt-6 text-red-400 font-semibold" title="마스킹 안내">개인정보 있어요. 마스킹 표시 해주세요</div>
        <div class="w-full border-none p-2 mt-6 mb-2 rounded min-h-[48px]" style="white-space:pre-wrap;word-break:break-all;">
          <span v-for="(part, i) in getMaskedParts()" :key="'part-'+i">
            <template v-if="part.type === 'pii'">
              <input type="checkbox" v-model="maskingState.masked[part.idx]" @change="updateMasked" class="no-focus-checkbox" style="margin-right:2px;vertical-align:middle;" tabindex="-1" title="마스킹" />
              <span class="font-mono bg-yellow-100 px-1 rounded" style="margin-right:2px;vertical-align:middle;">
                {{ getMaskedText(part, maskingState.masked[part.idx]) }} [{{ getMaskedTypeKor(part) }}]
              </span>
            </template>
            <template v-else>
              {{ part.text }}
            </template>
          </span>
        </div>
        <div class="flex justify-center mt-5 mb-2">
          <button 
            class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 min-w-[150px]" 
            :disabled="isWaitingResponse" 
            @click="resendWithMaskingOverride"
            title="개인정보 체크 후 전송"
          >
            개인정보 체크 후 전송
          </button>
        </div>
        <div v-if="isWaitingResponse && maskingState" class="flex flex-row items-center justify-center mt-6 h-6">
          <span class="flex items-center text-gray-500 font-semibold" style="font-size:1.1em;">
            <svg class="animate-spin h-5 w-5 text-gray-400 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
            답변을 작성 중입니다
          </span>
        </div>
      </template>
    </AlertModal>

    <!-- 필터링 모달 -->
    <FilteringModal
      :visible="showFilteringModal"
      :error-message="filteringErrorMessage"
      :loading="isWaitingResponse"
      :is-file-pii="filteringErrorCode === '2106'"
      :single-button="filteringSingleButton"
      @cancel="handleFilteringCancel"
      @confirm="resendWithFilteringOverride"
    />
  </div>
</template>

<script>
import AppHamburgerMenu from '@/components/navigation/HamburgerMenu.vue'
import AppUserMenu from '@/components/navigation/UserMenu.vue'
import AppModelSelect from '@/components/navigation/ModelSelect.vue'
import ChatInput from '@/components/chat/ChatInput.vue'
import AppSidebar from '@/components/navigation/Sidebar.vue'
import { authAPI, chatAPI, secureChatAPI } from '@/utils/api'
import userInfoMixin from '@/mixins/userInfo'
import ChatMessage from '@/components/chat/ChatMessage.vue'
import AlertModal from '@/components/common/AlertModal.vue'
import FilteringModal from '@/components/chat/FilteringModal.vue'
import { mapState, mapActions } from 'vuex'
import { getErrorMessage } from '@/utils/errorCodes'
import { getAllSupportedModels } from '@/utils/models'


export default {
  name: 'ModelPlayground',
  mixins: [userInfoMixin],
  components: {
    AppHamburgerMenu,
    AppUserMenu,
    AppModelSelect,
    ChatInput,
    AppSidebar,
    ChatMessage,
    AlertModal,
    FilteringModal
  },
  data() {
    return {
      leftModel: null,
      rightModel: null,
      leftModelPlaceholder: '모델 선택',
      rightModelPlaceholder: '모델 선택',
      leftMessages: [],
      rightMessages: [],
      tokenUsage: {
        percentage: 0,
        used: 0,
        total: 0
      },
      isWaitingResponse: false,
      currentResponseController: null,
      debugMode: false,
      openedDropdown: null,
      showModelSelectModal: false,
      showHistoryPanel: false,
      showAlertModal: false,
      alertMessage: '',
      maskingState: null,
      showMaskingModal: false,
      showFilteringModal: false,
      filteringErrorMessage: '',
      filteringErrorCode: '',
      filteringSingleButton: false,
      originalPayload: null,
      overrideOptions: {},
      isPaused: false,
      maskingParts: [] // 마스킹 파트 배열 추가
    }
  },
  computed: {
    ...mapState(['sidebarVisible']),
    canSendMessage() {
      return this.leftModel && this.rightModel;
    },
    isSidebarHidden() {
      return !this.sidebarVisible;
    }
  },
  methods: {
    ...mapActions(['toggleSidebar', 'setSidebarVisible']),
    toggleSidebar() {
      this.toggleSidebar();
    },
    selectLeftModel(model) {
      this.leftModel = model;
      this.leftModelPlaceholder = model.name;
      this.leftMessages = [];
      this.rightMessages = [];
    },
    selectRightModel(model) {
      this.rightModel = model;
      this.rightModelPlaceholder = model.name;
      this.leftMessages = [];
      this.rightMessages = [];
    },
    async handleEditLeftMessage({ messageId, content }) {
      try {
        const messageIndex = this.leftMessages.findIndex(msg => msg.id === messageId)
        if (messageIndex === -1) return
        // 메시지 업데이트
        const updatedMessage = {
          ...this.leftMessages[messageIndex],
          content,
          timestamp: Date.now()
        }
        this.leftMessages.splice(messageIndex, 1, updatedMessage)
      } catch (error) {
        console.error('메시지 수정 중 오류 발생:', error)
      }
    },
    async handleEditRightMessage({ messageId, content }) {
      try {
        const messageIndex = this.rightMessages.findIndex(msg => msg.id === messageId)
        if (messageIndex === -1) return
        // 메시지 업데이트
        const updatedMessage = {
          ...this.rightMessages[messageIndex],
          content,
          timestamp: Date.now()
        }
        this.rightMessages.splice(messageIndex, 1, updatedMessage)
      } catch (error) {
        console.error('메시지 수정 중 오류 발생:', error)
      }
    },
    validateBeforeSend(/* { content, files } */) {
      // 모델 미선택 시 사전 검증 실패
      if (!this.leftModel || !this.rightModel) {
        this.showModelSelectModal = true;
        return false; // 전송 중단
      }
      return true; // 전송 허용
    },
    async handleMessageSuccess(userMessage) {
        console.log('[ModelPlayground] handleMessageSuccess 진입', userMessage);
      
      const content = userMessage?.content || '';
      const files = userMessage?.files || [];
      
      if (!content && files.length === 0) return;
      if (this.isWaitingResponse) return;
      
      this.isWaitingResponse = true;
      
      try {
        // ✅ SDK 호출 직전 시간 기록 (메인 Chat.vue와 동일)
        const requestTime = Date.now();
        
        // 좌측 사용자 메시지
        const leftUserMessage = {
          id: 'user_' + requestTime + '_left',
          role: 'user',
          content: content,
          timestamp: requestTime,
          modelName: this.leftModel?.name || this.leftModel?.id || ''
        };
        
        // 우측 사용자 메시지  
        const rightUserMessage = {
          id: 'user_' + requestTime + '_right',
          role: 'user',
          content: content,
          timestamp: requestTime,
          modelName: this.rightModel?.name || this.rightModel?.id || ''
        };
        
        // 파일 첨부 처리
        if (files && files.length > 0) {
          const uniqueFiles = files.filter((f, i, arr) => 
            arr.findIndex(item => item.id === f.id) === i
          );
          
          const attachments = uniqueFiles.map(f => ({
            id: f.id,
            size: f.size,
            name: f.name,
            mime_type: f.mime_type || 'application/pdf'
          }));
          
          leftUserMessage.metadata = { attachments };
          rightUserMessage.metadata = { attachments };
        }
        
        // 사용자 메시지 즉시 추가
        this.leftMessages = [...this.leftMessages, leftUserMessage];
        this.rightMessages = [...this.rightMessages, rightUserMessage];
        
        // 로딩 메시지 추가
        const leftLoadingMsg = {
          id: 'loading_' + requestTime + '_left',
          role: 'loading',
          content: content,
          modelName: this.leftModel?.name || this.leftModel?.id || '',
          timestamp: null
        };
        
        const rightLoadingMsg = {
          id: 'loading_' + requestTime + '_right',
          role: 'loading',
          content: content,
          modelName: this.rightModel?.name || this.rightModel?.id || '',
          timestamp: null
        };
        
        this.leftMessages = [...this.leftMessages, leftLoadingMsg];
        this.rightMessages = [...this.rightMessages, rightLoadingMsg];
        this.scrollToBottom();
        
        // ✅ 입력창 즉시 초기화 (메시지 추가 후 바로)
        if (this.$refs.chatInput) {
          this.$refs.chatInput.resetState();
        }
        
        // ✅ 기존 우회 로직으로 서버 요청 (변경 없음)
        await this.sendMessageWithValidation(content, files, leftLoadingMsg, rightLoadingMsg);
        
      } catch (err) {
        console.error('[ModelPlayground] handleMessageSuccess 오류:', err);
        this.alertMessage = '잠시후 다시 시도해주세요';
        this.showAlertModal = true;
        this.isWaitingResponse = false;
      }
    },
    handlePause() {
      this.isPaused = true;
      if (this.currentResponseController) {
        this.currentResponseController.abort();
        this.currentResponseController = null;
      }
      this.isWaitingResponse = false;
      // 로딩 메시지 제거
      this.leftMessages = this.leftMessages.filter(m => m.role !== 'loading');
      this.rightMessages = this.rightMessages.filter(m => m.role !== 'loading');
    },
    // 스크롤을 맨 아래로 이동하는 메소드 추가
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.leftChatContainer) {
          this.$refs.leftChatContainer.scrollTop = this.$refs.leftChatContainer.scrollHeight
        }
        if (this.$refs.rightChatContainer) {
          this.$refs.rightChatContainer.scrollTop = this.$refs.rightChatContainer.scrollHeight
        }
      })
    },
    // 좌측 응답 처리 함수
    async processLeftResponse(leftResponse, leftLoadingMsg) {
      const leftLoadingIdx = this.leftMessages.findIndex(m => m.id === leftLoadingMsg.id);
      
      // 에러 응답 처리
      if (leftResponse?.status && leftResponse.status !== 200) {
        // console.log('[ModelPlayground] 좌측 에러 응답:', leftResponse);
        // console.log('[ModelPlayground] leftLoadingIdx:', leftLoadingIdx);
        const errorMessage = await (this.$refs.leftChatMessage?.handleErrorResponse(leftResponse) || Promise.resolve('에러가 발생했습니다.'));
        // console.log('[ModelPlayground] 좌측 에러 메시지:', errorMessage);
        if (leftLoadingIdx !== -1) {
          this.leftMessages.splice(leftLoadingIdx, 1, {
            id: 'assistant_error_' + Date.now(),
            role: 'assistant',
            content: errorMessage,
            timestamp: Date.now(),
            modelName: this.leftModel?.name || this.leftModel?.id || '',
            isError: true
          });
          // console.log('[ModelPlayground] 좌측 메시지 교체 완료');
        }
        return;
      }
      
      let leftContent = leftResponse?.content || leftResponse?.message || '';
      if (typeof leftContent !== 'string') leftContent = JSON.stringify(leftContent);
      // Chat.vue/ChatMessage.vue처럼 JSON 파싱 후 content만 추출
      try {
        const obj = JSON.parse(leftContent);
        if (obj && typeof obj === 'object' && obj.content) leftContent = obj.content;
      } catch (e) { /* 무시 */ }
      
      if (!leftResponse || (!leftResponse.content && !leftResponse.message)) {
        if (leftLoadingIdx !== -1) {
          this.leftMessages.splice(leftLoadingIdx, 1, {
            id: 'assistant_error_' + Date.now(),
            role: 'assistant',
            content: '현재 선택한 모델에서 일시적으로 응답이 지연되거나 연결이 원활하지 않습니다. 잠시 후 다시 시도해 주세요.',
            timestamp: null,
            modelName: this.leftModel?.name || this.leftModel?.id || ''
          });
        }
      } else {
        if (leftLoadingIdx !== -1) this.leftMessages.splice(leftLoadingIdx, 1);
        this.leftMessages = this.leftMessages.filter(m => m.role !== 'loading');
        
        // ✅ created_at 시간을 미리 저장 (타이핑 완료 후 사용)
        let responseTimestamp = Date.now(); // 기본값
        if (leftResponse.created_at) {
          responseTimestamp = new Date(leftResponse.created_at).getTime();
        }
        
        const assistantMsg = {
          id: 'assistant_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
          role: 'assistant',
          content: '',
          timestamp: null,
          modelName: this.leftModel?.name || this.leftModel?.id || '',
          encryptedContent: leftResponse?.message || leftResponse?.encryptedContent || '',
          metadata: leftResponse?.metadata || null,
          reference_docs: []
        };
        this.leftMessages = [...this.leftMessages, assistantMsg];
        const assistantIdx = this.leftMessages.length - 1;
        
        // 새 메시지 추가 후 즉시 스크롤
        this.scrollToBottom();
        
        let displayed = '';
        for (let i = 0; i < leftContent.length; i++) {
          if (this.isPaused) break;
          displayed += leftContent[i];
          this.$set(this.leftMessages, assistantIdx, {
            ...this.leftMessages[assistantIdx],
            content: displayed
          });
          
          // 타이핑 효과 중 주기적으로 스크롤 업데이트 (10글자마다)
          if (i % 10 === 0 || i === leftContent.length - 1) {
            this.scrollToBottom();
          }
          
          await new Promise(res => setTimeout(res, 15));
        }
        // ✅ 타이핑 완료 후 created_at 시간과 reference_docs 함께 설정 (메인 Chat.vue와 동일)
        this.$set(this.leftMessages, assistantIdx, {
          ...this.leftMessages[assistantIdx],
          timestamp: responseTimestamp,
          reference_docs: leftResponse?.reference_docs || []
        });
      }
      this.isPaused = false;
    },
    // 우측 응답 처리 함수
    async processRightResponse(rightResponse, rightLoadingMsg) {
      const rightLoadingIdx = this.rightMessages.findIndex(m => m.id === rightLoadingMsg.id);
      
      // 에러 응답 처리
      if (rightResponse?.status && rightResponse.status !== 200) {
        // console.log('[ModelPlayground] 우측 에러 응답:', rightResponse);
        // console.log('[ModelPlayground] rightLoadingIdx:', rightLoadingIdx);
        const errorMessage = await (this.$refs.rightChatMessage?.handleErrorResponse(rightResponse) || Promise.resolve('에러가 발생했습니다.'));
        // console.log('[ModelPlayground] 우측 에러 메시지:', errorMessage);
        if (rightLoadingIdx !== -1) {
          this.rightMessages.splice(rightLoadingIdx, 1, {
            id: 'assistant_error_' + Date.now(),
            role: 'assistant',
            content: errorMessage,
            timestamp: Date.now(),
            modelName: this.rightModel?.name || this.rightModel?.id || '',
            isError: true
          });
          // console.log('[ModelPlayground] 우측 메시지 교체 완료');
        }
        return;
      }
      
      let rightContent = rightResponse?.content || rightResponse?.message || '';
      if (typeof rightContent !== 'string') rightContent = JSON.stringify(rightContent);
      // Chat.vue/ChatMessage.vue처럼 JSON 파싱 후 content만 추출
      try {
        const obj = JSON.parse(rightContent);
        if (obj && typeof obj === 'object' && obj.content) rightContent = obj.content;
      } catch (e) { /* 무시 */ }
      
      if (!rightResponse || (!rightResponse.content && !rightResponse.message)) {
        if (rightLoadingIdx !== -1) {
          this.rightMessages.splice(rightLoadingIdx, 1, {
            id: 'assistant_error_' + Date.now(),
            role: 'assistant',
            content: '현재 선택한 모델에서 일시적으로 응답이 지연되거나 연결이 원활하지 않습니다. 잠시 후 다시 시도해 주세요.',
            timestamp: null,
            modelName: this.rightModel?.name || this.rightModel?.id || ''
          });
        }
      } else {
        if (rightLoadingIdx !== -1) this.rightMessages.splice(rightLoadingIdx, 1);
        this.rightMessages = this.rightMessages.filter(m => m.role !== 'loading');
        
        // ✅ created_at 시간을 미리 저장 (타이핑 완료 후 사용)
        let responseTimestamp = Date.now(); // 기본값
        if (rightResponse.created_at) {
          responseTimestamp = new Date(rightResponse.created_at).getTime();
        }
        
        const assistantMsg = {
          id: 'assistant_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
          role: 'assistant',
          content: '',
          timestamp: null,
          modelName: this.rightModel?.name || this.rightModel?.id || '',
          encryptedContent: rightResponse?.message || rightResponse?.encryptedContent || '',
          metadata: rightResponse?.metadata || null,
          reference_docs: []
        };
        this.rightMessages = [...this.rightMessages, assistantMsg];
        const assistantIdx = this.rightMessages.length - 1;
        
        // 새 메시지 추가 후 즉시 스크롤
        this.scrollToBottom();
        
        let displayed = '';
        for (let i = 0; i < rightContent.length; i++) {
          if (this.isPaused) break;
          displayed += rightContent[i];
          this.$set(this.rightMessages, assistantIdx, {
            ...this.rightMessages[assistantIdx],
            content: displayed
          });
          
          // 타이핑 효과 중 주기적으로 스크롤 업데이트 (10글자마다)
          if (i % 10 === 0 || i === rightContent.length - 1) {
            this.scrollToBottom();
          }
          
          await new Promise(res => setTimeout(res, 15));
        }
        // ✅ 타이핑 완료 후 created_at 시간과 reference_docs 함께 설정 (메인 Chat.vue와 동일)
        this.$set(this.rightMessages, assistantIdx, {
          ...this.rightMessages[assistantIdx],
          timestamp: responseTimestamp,
          reference_docs: rightResponse?.reference_docs || []
        });
      }
      this.isPaused = false;
    },
    async logout() {
      // 로그아웃 요청을 기다리지 않고 바로 리다이렉트
      this.$router.push('/login')
      // 백그라운드에서 로그아웃 처리
      try {
        await authAPI.logout()
      } catch (error) {
        console.error('로그아웃 중 오류 발생:', error)
      }
    },
    onDropdownOpened(type) {
      this.openedDropdown = type;
    },
    closeAllDropdowns() {
      this.openedDropdown = null;
    },
    handleDocumentClick(e) {
      if (!this.$el.contains(e.target)) {
        this.closeAllDropdowns();
      }
    },
    openSidebar() {
      this.setSidebarVisible(true);
    },
    closeSidebar() {
      this.setSidebarVisible(false);
    },
    async createNewChat() {
      await this.$router.push({ path: '/chat', query: { new: 1 } });
      this.$nextTick(() => {
        window.dispatchEvent(new CustomEvent('force-new-chat'));
      });
    },
    getMaskedParts() {
      if (!this.maskingState) return [];
      const { userContent, detectedPii } = this.maskingState;
      let parts = [];
      let lastIdx = 0;
      detectedPii.forEach((item, i) => {
        const start = item.start;
        const end = item.end;
        if (start > lastIdx) {
          parts.push({ type: 'text', text: userContent.slice(lastIdx, start) });
        }
        parts.push({
          type: 'pii',
          idx: i,
          text: userContent.slice(start, end),
          entityType: item.entityType,
          maskedText: item.maskedText
        });
        lastIdx = end;
      });
      if (lastIdx < userContent.length) {
        parts.push({ type: 'text', text: userContent.slice(lastIdx) });
      }
      return parts;
    },
    getMaskedText(item, masked) {
      if (!masked) return item.text;
      if (item.entityType === 'KR_PHONE') {
        return item.text.replace(/(\d{3,4})-(\d{3,4})-(\d{4})/, '$1-$2-****');
      }
      if (item.entityType === 'KR_ID') {
        return item.text.replace(/(\d{6})-(\d{1})(\d{6})/, '$1-$2******');
      }
      if (item.entityType === 'KR_PERSON') {
        return item.maskedText || '<이름>';
      }
      // 기타: 전체 마스킹
      return '*'.repeat(item.text.length);
    },
    getMaskedTypeKor(item) {
      if (item.entityType === 'KR_PHONE') {
        return '전화번호';
      }
      if (item.entityType === 'KR_ID') {
        return '주민번호';
      }
      if (item.entityType === 'KR_PERSON') {
        return '이름';
      }
      if (item.entityType === 'KR_EMAIL') {
        return '이메일';
      }
      if (item.entityType === 'KR_ADDRESS') {
        return '주소';
      }
      return '개인정보';
    },
    updateMasked() {
      if (!this.maskingState) return;
      const { userContent, detectedPii, masked } = this.maskingState;
      let result = '';
      let lastIdx = 0;
      detectedPii.forEach((item, i) => {
        const start = item.start;
        const end = item.end;
        result += userContent.slice(lastIdx, start);
        if (masked[i]) {
          result += this.getMaskedText({
            text: userContent.slice(start, end),
            entityType: item.entityType,
            maskedText: item.maskedText
          }, true);
        } else {
          result += userContent.slice(start, end);
        }
        lastIdx = end;
      });
      result += userContent.slice(lastIdx);
      this.maskingState.maskedUserContent = result;
    },
    getMaskedUserContent() {
      if (!this.maskingState) return '';
      const { userContent, detectedPii, masked } = this.maskingState;
      let result = '';
      let lastIdx = 0;
      detectedPii.forEach((item, i) => {
        const start = item.start;
        const end = item.end;
        result += userContent.slice(lastIdx, start);
        if (masked[i]) {
          result += this.getMaskedText({
            text: userContent.slice(start, end),
            entityType: item.entityType,
            maskedText: item.maskedText
          }, true);
        } else {
          result += userContent.slice(start, end);
        }
        lastIdx = end;
      });
      result += userContent.slice(lastIdx);
      return result;
    },
    // PII 검증 처리
    async handlePiiValidation(response, hasToxicFilter = false) {
      try {
        const add = response.error.additional;
        const detected = add.detectedPii || [];
        
        this.maskingState = {
          maskedUserContent: add.maskedUserContent || '',
          userContent: add.userContent || '',
          detectedPii: detected,
          masked: detected.map(() => true),
          hasToxicFilter // 필터링 동시 발생 여부
        };
        
        this.showAlertModal = false;
        this.showMaskingModal = true;
        this.isWaitingResponse = false;
      } catch (err) {
        console.error('[ModelPlayground.vue] PII 검증 처리 중 오류:', err);
        this.alertMessage = '검증 처리 중 오류가 발생했습니다.';
        this.showAlertModal = true;
        this.isWaitingResponse = false;
      }
    },
    // 필터링 검증 처리
    async handleFilteringValidation(response) {
      try {
        this.filteringErrorCode = response.error?.code;
        this.filteringErrorMessage = getErrorMessage(this.filteringErrorCode);
        
        this.showAlertModal = false;
        this.showFilteringModal = true;
        this.isWaitingResponse = false;
      } catch (err) {
        console.error('[ModelPlayground.vue] 필터링 검증 처리 중 오류:', err);
        this.alertMessage = '검증 처리 중 오류가 발생했습니다.';
        this.showAlertModal = true;
        this.isWaitingResponse = false;
      }
    },
    // 마스킹 재전송
    async resendWithMaskingOverride() {
      try {
        this.isWaitingResponse = true;
        
        const maskedContent = this.getMaskedUserContent();
        if (!maskedContent || maskedContent.trim().length === 0) {
          this.alertMessage = '마스킹된 메시지가 비어 있습니다. 다시 입력해 주세요.';
          this.showAlertModal = true;
          this.isWaitingResponse = false;
          return;
        }

        // 우회 옵션 누적 (기존 옵션 유지하면서 추가)
        const updatedOverrides = {
          ...this.overrideOptions,
          overridePrivacyFilter: true
        };

        // 필터링도 함께 있었다면 필터링 우회도 추가
        if (this.maskingState.hasToxicFilter) {
          updatedOverrides.overrideToxicFilter = true;
        }

        // 마스킹 모달 닫기
        this.showMaskingModal = false;
        this.maskingState = null;
        
        // 우회 옵션 상태 업데이트
        this.overrideOptions = updatedOverrides;
        
        console.log('[ModelPlayground] finalOverrides:', updatedOverrides);
        
        // 마스킹된 내용으로 메시지 재전송
        await this.sendMessageWithValidation(maskedContent, []);

      } catch (err) {
        console.error('[ModelPlayground.vue] 마스킹 재전송 중 오류:', err);
        this.alertMessage = '메시지 전송 중 오류가 발생했습니다.';
        this.showAlertModal = true;
        this.isWaitingResponse = false;
      }
    },
    // 필터링 재전송
    async resendWithFilteringOverride() {
      try {
        this.isWaitingResponse = true;

        // 파일 PII(2106)인 경우 overridePrivacyFilter 사용
        const currentOverride = this.filteringErrorCode === '2106'
          ? { overridePrivacyFilter: true }
          : { overrideToxicFilter: true };

        // 우회 옵션 누적 (기존 옵션 유지하면서 추가)
        const updatedOverrides = {
          ...this.overrideOptions,
          ...currentOverride
        };

        // 필터링 모달 닫기
        this.showFilteringModal = false;
        this.filteringErrorCode = '';
        this.filteringErrorMessage = '';
        
        // 우회 옵션 상태 업데이트
        this.overrideOptions = updatedOverrides;
        
        // 원본 내용으로 메시지 재전송
        const content = this.originalPayload?.messages?.[0]?.content || '';
        await this.sendMessageWithValidation(content, []);

      } catch (err) {
        console.error('[ModelPlayground.vue] 필터링 재전송 중 오류:', err);
        this.alertMessage = '메시지 전송 중 오류가 발생했습니다.';
        this.showAlertModal = true;
        this.isWaitingResponse = false;
      }
    },
    // 필터링 모달 취소 처리
    handleFilteringCancel() {
      this.resetValidationState();
    },
    // 검증 상태 초기화
    resetValidationState() {
      this.isWaitingResponse = false;
      this.originalPayload = null;
      this.maskingState = null;
      this.showMaskingModal = false;
      this.showFilteringModal = false;
      this.filteringErrorMessage = '';
      this.filteringErrorCode = '';
      this.overrideOptions = {};
    },
    // 검증 처리를 포함한 메시지 전송
    async sendMessageWithValidation(content, files = [], leftLoadingMsg, rightLoadingMsg) {
      try {
        // 원본 payload 생성 및 저장
        const payload = {
          model: 'gemma3:4b', // 기본 모델
          messages: [
            {
              role: 'user',
              content: content.trim(),
              metadata: {
                attachments: files.map(f => ({
                  id: f.id,
                  name: f.name,
                  size: f.size,
                  mime_type: f.mime_type || 'application/pdf'
                }))
              }
            }
          ],
          stream: false,
          storeChat: false
        };
        
        if (!this.originalPayload) {
          this.originalPayload = payload;
        }
        
        // 기본 우회 옵션과 전달받은 overrides 병합
        const defaultOverrides = {
          overridePrivacyFilter: true,
          overrideToxicFilter: true
        };
        const finalOverrides = {
          ...defaultOverrides
        };
        
        console.log('[ModelPlayground] finalOverrides:', finalOverrides);
        
        // 좌/우 모델로 각각 전송 시도
        const [leftResponse, rightResponse] = await Promise.allSettled([
          secureChatAPI.sendMessage('playground_session_left', {
            ...payload,
            model: this.leftModel?.id
          }, finalOverrides).catch(err => err),
          secureChatAPI.sendMessage('playground_session_right', {
            ...payload,
            model: this.rightModel?.id
          }, finalOverrides).catch(err => err)
        ]).then(results => [
          results[0].status === 'fulfilled' ? results[0].value : results[0].reason,
          results[1].status === 'fulfilled' ? results[1].value : results[1].reason
        ]);

        // 422 에러는 processLeftResponse/processRightResponse에서 처리하도록 위임
        // 중복 처리를 방지하기 위해 여기서는 특수 처리하지 않음

        // 에러/정상 상관없이 공통 처리: 각 사이드 처리 함수에 위임
        if ((leftResponse?.status && leftResponse.status !== 200) || 
            (rightResponse?.status && rightResponse.status !== 200)) {
          await Promise.all([
            this.processLeftResponse(leftResponse, leftLoadingMsg),
            this.processRightResponse(rightResponse, rightLoadingMsg)
          ]);
          this.scrollToBottom();
          this.isWaitingResponse = false;
          return;
        }

        // 정상 응답 처리
        await this.processSuccessfulResponses(leftResponse, rightResponse, content, files, leftLoadingMsg, rightLoadingMsg);
        
      } catch (err) {
        console.error('[ModelPlayground] sendMessageWithValidation 오류:', err);
        this.alertMessage = '메시지 전송 중 오류가 발생했습니다.';
        this.showAlertModal = true;
      } finally {
        this.isWaitingResponse = false;
        this.currentResponseController = null;
        
        // 토큰 사용량 갱신
        try {
          const usageRes = await chatAPI.getTokenUsage();
          if (usageRes && usageRes.data) {
            const data = usageRes.data;
            let percentage = 0;
            
            if (typeof data.percentage === 'number' && !isNaN(data.percentage) && isFinite(data.percentage)) {
              percentage = Math.max(0, Math.min(100, data.percentage));
            } else if (typeof data.used === 'number' && typeof data.total === 'number' && data.total > 0) {
              const calculated = (data.used / data.total) * 100;
              if (!isNaN(calculated) && isFinite(calculated)) {
                percentage = Math.max(0, Math.min(100, calculated));
              }
            }
            
            this.tokenUsage = {
              percentage: percentage,
              used: typeof data.used === 'number' && !isNaN(data.used) && isFinite(data.used) ? data.used : 0,
              total: typeof data.total === 'number' && !isNaN(data.total) && isFinite(data.total) ? data.total : 0
            };
          }
        } catch (e) {
          this.tokenUsage = {
            percentage: 0,
            used: 0,
            total: 0
          };
        }
        
        // 입력창 포커스
        this.$nextTick(() => {
          try {
            const chatInput = this.$refs.chatInput;
            if (chatInput && chatInput.$refs && chatInput.$refs.messageInput) {
              chatInput.$refs.messageInput.focus();
            }
          } catch (focusError) {
            console.log('입력창 포커스 설정 중 오류:', focusError);
          }
        });
      }
    },
    // 성공적인 응답 처리
    async processSuccessfulResponses(leftResponse, rightResponse, content, files, leftLoadingMsg, rightLoadingMsg) {
      try {
        // ✅ 사용자 메시지와 로딩 메시지는 이미 추가되어 있으므로 응답 처리만 수행
        
        // 좌측과 우측 응답을 동시에 처리
        await Promise.all([
          this.processLeftResponse(leftResponse, leftLoadingMsg),
          this.processRightResponse(rightResponse, rightLoadingMsg)
        ]);
        
        this.scrollToBottom();
        
      } catch (err) {
        console.error('[ModelPlayground] processSuccessfulResponses 오류:', err);
        this.alertMessage = '응답 처리 중 오류가 발생했습니다.';
        this.showAlertModal = true;
      }
    },
    
    // 에러 모달 핸들러
    handleErrorModal(errorMessage) {
      this.alertMessage = errorMessage;
      this.showAlertModal = true;
    },
    
    // 필터링 모달 핸들러
    handleFilteringModal(data) {
      console.log('[ModelPlayground] handleFilteringModal 호출됨:', data);
      this.filteringErrorCode = data.errorCode;
      this.filteringErrorMessage = data.errorMessage;
      this.filteringSingleButton = data.singleButton;
      this.showFilteringModal = true;
    },
    
    // 마스킹 모달 핸들러
    async handleMaskingModal(data) {
      try {
        const response = data.response;
        const hasToxicFilter = data.hasToxicFilter;
        
        const add = response.error.additional;
        const detected = add.detectedPii || [];
        
        this.maskingState = {
          maskedUserContent: add.maskedUserContent || '',
          userContent: add.userContent || '',
          detectedPii: detected,
          masked: detected.map(() => true),
          hasToxicFilter // 필터링 동시 발생 여부
        };
        
        // 마스킹 파트 배열 초기화
        this.initializeMaskingParts();
        
        this.showAlertModal = false;
        this.showMaskingModal = true;
      } catch (err) {
        console.error('[ModelPlayground.vue] 마스킹 모달 처리 중 오류:', err);
        this.alertMessage = '마스킹 처리 중 오류가 발생했습니다.';
        this.showAlertModal = true;
      }
    },
    
    // 마스킹 파트 배열 초기화
    initializeMaskingParts() {
      if (!this.maskingState || !this.maskingState.detectedPii) {
        this.maskingParts = [];
        return;
      }

      const parts = [];
      let currentIndex = 0;
      const userContent = this.maskingState.userContent || '';

      this.maskingState.detectedPii.forEach((pii, index) => {
        // PII 이전 텍스트 추가
        if (pii.start > currentIndex) {
          parts.push({
            type: 'text',
            text: userContent.substring(currentIndex, pii.start),
            index: parts.length
          });
        }

        // PII 추가
        parts.push({
          type: 'pii',
          originalText: pii.originalText,
          maskedText: pii.maskedText,
          entityType: pii.entityType,
          index: index,
          isPii: true
        });

        currentIndex = pii.end;
      });

      // 마지막 텍스트 추가
      if (currentIndex < userContent.length) {
        parts.push({
          type: 'text',
          text: userContent.substring(currentIndex),
          index: parts.length
        });
      }

      this.maskingParts = parts;
    },

    // 랜덤 모델 선택
    async selectRandomModels() {
      try {
        // utils/models.js에서 지원되는 모델 목록 가져오기
        const availableModels = getAllSupportedModels();

        if (availableModels.length >= 2) {
          // 배열을 섞어서 랜덤하게 2개 선택
          const shuffled = [...availableModels].sort(() => Math.random() - 0.5);
          const leftModel = shuffled[0];
          const rightModel = shuffled[1];

          // 왼쪽 모델 설정
          this.leftModel = leftModel;
          this.leftModelPlaceholder = leftModel.name;

          // 오른쪽 모델 설정
          this.rightModel = rightModel;
          this.rightModelPlaceholder = rightModel.name;

          console.log(`[ModelPlayground] 랜덤 모델 선택됨 - 왼쪽: ${leftModel.name}, 오른쪽: ${rightModel.name}`);
        }
      } catch (error) {
        console.error('[ModelPlayground] 랜덤 모델 선택 중 오류:', error);
        // 오류 시에는 기본 동작 유지 (모달 표시하지 않음)
      }
    },
  },
  async mounted() {
    try {
      const hasUserInfo = await this.fetchUserInfo()
      if (hasUserInfo) {

        // ✅ 기본 SDK로 복구 (Role Playground에서 오염되었을 가능성 대비)
                  try {
            // SDK는 main.js에서 Vue.prototype.$sdk로 설정됨
          } catch (e) {
          console.error('[ModelPlayground] SDK 복구 중 오류:', e);
        }
        try {
          const usageRes = await chatAPI.getTokenUsage();
          if (usageRes && usageRes.data) {
            // validator 조건에 맞게 데이터 검증 및 변환
            const data = usageRes.data;
            let percentage = 0;

            // percentage 값 안전하게 처리
            if (typeof data.percentage === 'number' && !isNaN(data.percentage) && isFinite(data.percentage)) {
              percentage = Math.max(0, Math.min(100, data.percentage));
            } else if (typeof data.used === 'number' && typeof data.total === 'number' && data.total > 0) {
              // percentage가 없으면 used/total로 계산
              const calculated = (data.used / data.total) * 100;
              if (!isNaN(calculated) && isFinite(calculated)) {
                percentage = Math.max(0, Math.min(100, calculated));
              }
            }

            this.tokenUsage = {
              percentage: percentage,
              used: typeof data.used === 'number' && !isNaN(data.used) && isFinite(data.used) ? data.used : 0,
              total: typeof data.total === 'number' && !isNaN(data.total) && isFinite(data.total) ? data.total : 0
            };
          }
        } catch (e) {
          // 오류 시 안전한 기본값으로 설정
          this.tokenUsage = {
            percentage: 0,
            used: 0,
            total: 0
          };
        }
      }
    } catch (error) {
      // 데이터 조회 중 오류 발생 시 콘솔에만 출력
      // console.error('데이터 조회 중 오류 발생:', error)
    }
    document.addEventListener('click', this.handleDocumentClick);

    // 모델이 선택되지 않았을 때 랜덤 선택
    if (!this.leftModel || !this.rightModel) {
      await this.selectRandomModels();
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
  }
}
</script>

<style>
.slide-enter-active,
.slide-leave-active {
  transition: width 0.3s ease;
}

/* 체크박스 기본 스타일 완전 오버라이드 */
.no-focus-checkbox {
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  width: 16px !important;
  height: 16px !important;
  border: 1px solid #ccc !important;
  border-radius: 3px !important;
  background: white !important;
  outline: none !important;
  box-shadow: none !important;
  cursor: pointer !important;
  display: inline-block !important;
}

.no-focus-checkbox:checked {
  background: #007bff !important;
  border-color: #007bff !important;
}

.no-focus-checkbox:checked::after {
  color: white !important;
  font-size: 12px !important;
  position: absolute !important;
  top: -1px !important;
  left: 2px !important;
  line-height: 1 !important;
}

.no-focus-checkbox:focus,
.no-focus-checkbox:focus-visible,
.no-focus-checkbox:active,
.no-focus-checkbox:hover:focus,
.no-focus-checkbox:hover {
  outline: none !important;
  box-shadow: none !important;
  border-color: #ccc !important;
  background: white !important;
}

.no-focus-checkbox:checked:focus,
.no-focus-checkbox:checked:focus-visible,
.no-focus-checkbox:checked:active,
.no-focus-checkbox:checked:hover:focus,
.no-focus-checkbox:checked:hover {
  background: #007bff !important;
  border-color: #007bff !important;
  outline: none !important;
  box-shadow: none !important;
}
</style>